"use client"

import type { JobAdsResult } from "@/modules/jobAds/client/api/types"
import type { PageContextValue } from "@/modules/jobSearch/components/PageContext/PageContextProvider"
import type { FunctionComponent, PropsWithChildren } from "react"

import { useRouter } from "next/navigation"
import { useMemo } from "react"

import { PageContextProvider } from "@/modules/jobSearch/components/PageContext/PageContextProvider"
import { Tracker } from "@/modules/jobSearch/components/Tracker/Tracker"

type InteractivePageProps = PropsWithChildren<{
  algoliaSearchId: JobAdsResult["searchId"]
  careerCenterConf: PageContextValue["careerCenterConf"]
  careerCenterId: PageContextValue["careerCenterId"]
  careerCenterLogo: PageContextValue["careerCenterLogo"]
  careerCenterMainId: PageContextValue["careerCenterMainId"]
  careerCenterShortName: PageContextValue["careerCenterShortName"]
  currentUserSearchConfig: PageContextValue["currentUserSearchConfig"]
  facets: PageContextValue["facets"]
  hasUserDailyJobOffersAlert: PageContextValue["hasUserDailyJobOffersAlert"]
  isDirectAlgoliaConnectionEnabled: PageContextValue["isDirectAlgoliaConnectionEnabled"]
  isJobAdsFoWoosmapMigrationEnabled: PageContextValue["isJobAdsFoWoosmapMigrationEnabled"]
  isSearchProxyV3ConnectionEnabled: PageContextValue["isSearchProxyV3ConnectionEnabled"]
  isPublicationDateFilterEnabled: PageContextValue["isPublicationDateFilterEnabled"]
  isUserLogged: PageContextValue["isUserLogged"]
  nbResults: JobAdsResult["totalSize"]
  prefilteredSearchParams: PageContextValue["prefilteredSearchParams"]
  trackingContext: PageContextValue["trackingContext"]
}>

export const InteractivePage: FunctionComponent<InteractivePageProps> = ({
  algoliaSearchId,
  careerCenterConf,
  careerCenterId,
  careerCenterLogo,
  careerCenterMainId,
  careerCenterShortName,
  children,
  currentUserSearchConfig,
  facets,
  hasUserDailyJobOffersAlert,
  isDirectAlgoliaConnectionEnabled,
  isJobAdsFoWoosmapMigrationEnabled,
  isSearchProxyV3ConnectionEnabled,
  isPublicationDateFilterEnabled,
  isUserLogged,
  nbResults,
  prefilteredSearchParams,
  trackingContext,
}) => {
  const { push } = useRouter()
  const pageContextValue: PageContextValue = useMemo(
    () => ({
      careerCenterConf,
      careerCenterId,
      careerCenterLogo,
      careerCenterMainId,
      careerCenterShortName,
      currentUserSearchConfig,
      facets,
      hasUserDailyJobOffersAlert,
      isDirectAlgoliaConnectionEnabled,
      isJobAdsFoWoosmapMigrationEnabled,
      isPublicationDateFilterEnabled,
      isSavedSearchNewJobAdsSinceLastSeenFeatureEnabled: false,
      isSearchProxyV3ConnectionEnabled,
      isUserLogged,
      prefilteredSearchParams,
      push,
      savedSearch: null,
      savedSearchSize: 0,
      trackingContext,
    }),
    [
      careerCenterConf,
      careerCenterId,
      careerCenterLogo,
      careerCenterMainId,
      careerCenterShortName,
      currentUserSearchConfig,
      facets,
      hasUserDailyJobOffersAlert,
      isDirectAlgoliaConnectionEnabled,
      isJobAdsFoWoosmapMigrationEnabled,
      isSearchProxyV3ConnectionEnabled,
      isPublicationDateFilterEnabled,
      isUserLogged,
      prefilteredSearchParams,
      push,
      trackingContext,
    ]
  )

  return (
    <PageContextProvider value={pageContextValue}>
      <Tracker algoliaSearchId={algoliaSearchId} nbResults={nbResults}>
        {children}
      </Tracker>
    </PageContextProvider>
  )
}
